import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchHistoricalData, exportCsvReport } from '../services/apiService';
import {
  formatRecordTimeFromRow,
  getRowDateTime,
  getRowTimeMs
} from '../utils/dateTimeFormat';
import './ReportGenerator.css';
import './ReportPage.css';

// Auto-load: refresh report data at this interval (same idea as template).
const REFRESH_INTERVAL_MS = 120000;

// Match backend max history cap (100000000) so the report can load all rows in range.
const REPORT_LIMIT = 100000000;

const DEFAULT_ROWS_PER_PAGE = 10;

// Sensor types — values match ReportGenerator / backend semantics
const SENSOR_TYPES = [
  { value: 'all', label: 'All Sensors' },
  { value: 'kseb', label: 'KSEB' },
  { value: 'inverter', label: 'Inverter' },
  { value: 'loadon', label: 'Load On' },
  { value: 'current', label: 'Current' },
  { value: 'temperature', label: 'Temperature' },
  { value: 'battery', label: 'Battery Voltage' },
  { value: 'panel', label: 'Panel Voltage' }
];

/** Full calendar day in the time pickers: 12:00 AM → 11:59 PM (inclusive through 23:59:59 on the API). */
const DEFAULT_REPORT_START_TIME = '00:00';
const DEFAULT_REPORT_END_TIME = '23:59';

function formatTimeForApiStart(htmlTime) {
  const t = String(htmlTime || '').trim();
  if (!t) return null;
  if (/^\d{2}:\d{2}:\d{2}$/.test(t)) return t;
  if (/^\d{2}:\d{2}$/.test(t)) return `${t}:00`;
  return `${t}:00`;
}

function formatTimeForApiEnd(htmlTime) {
  const t = String(htmlTime || '').trim();
  if (!t) return null;
  if (/^\d{2}:\d{2}:\d{2}$/.test(t)) {
    if (t === '23:59:00') return '23:59:59';
    return t;
  }
  if (/^\d{2}:\d{2}$/.test(t)) {
    if (t === '23:59') return '23:59:59';
    return `${t}:00`;
  }
  return `${t}:00`;
}

function localDateFromYmdAndHms(yyyyMmDd, hhmmss) {
  const [y, mo, d] = String(yyyyMmDd || '')
    .split('-')
    .map((x) => parseInt(x, 10));
  if (!y || !mo || !d) return null;
  const parts = String(hhmmss || '').split(':');
  const h = parseInt(parts[0], 10) || 0;
  const mi = parseInt(parts[1], 10) || 0;
  const s = parseInt(parts[2] || '0', 10) || 0;
  const dt = new Date(y, mo - 1, d, h, mi, s, 0);
  return isNaN(dt.getTime()) ? null : dt;
}

function getReportWindowBounds(f) {
  const startD = f.startDate || f.endDate;
  const endD = f.endDate || f.startDate;
  if (!startD || !endD) return null;
  const startClock = (f.startTime || '').trim() || DEFAULT_REPORT_START_TIME;
  const endClock = (f.endTime || '').trim() || DEFAULT_REPORT_END_TIME;
  const startTs = formatTimeForApiStart(startClock);
  const endTs = formatTimeForApiEnd(endClock);
  if (!startTs || !endTs) return null;
  const from = localDateFromYmdAndHms(startD, startTs);
  const to = localDateFromYmdAndHms(endD, endTs);
  if (!from || !to) return null;
  return { from, to };
}

function filterReadingsByRecordedAtWindow(rows, f) {
  if (!rows || rows.length === 0) return rows;
  const bounds = getReportWindowBounds(f);
  if (!bounds) return rows;
  return rows.filter((row) => {
    const t = getRowDateTime(row);
    if (!t) return false;
    return t >= bounds.from && t <= bounds.to;
  });
}

function buildReportHistoryParams(f, limit) {
  const params = {};
  if (limit != null) params.limit = limit;
  if (f.organizationId) params.organizationId = parseInt(f.organizationId, 10);
  if (f.startDate) {
    params.startDate = f.startDate;
    const startClock = (f.startTime || '').trim() || DEFAULT_REPORT_START_TIME;
    const st = formatTimeForApiStart(startClock);
    if (st) params.startTime = st;
  }
  if (f.endDate) params.endDate = f.endDate;
  else if (f.startDate) params.endDate = f.startDate;
  if (params.endDate) {
    const endClock = (f.endTime || '').trim() || DEFAULT_REPORT_END_TIME;
    const et = formatTimeForApiEnd(endClock);
    if (et) params.endTime = et;
  }
  return params;
}

function ReportPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    sensorType: 'all',
    minValue: '',
    maxValue: '',
    organizationId: ''
  });
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: DEFAULT_ROWS_PER_PAGE
  });
  const [exporting, setExporting] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const today = new Date().toISOString().split('T')[0];

  const loadReadings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const f = filtersRef.current;
      const params = buildReportHistoryParams(f, REPORT_LIMIT);
      const data = await fetchHistoricalData(params);
      setReadings(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load report data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReadings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchTrigger]);

  useEffect(() => {
    const s = location.state;
    if (!s || typeof s !== 'object' || (!s.startDate && !s.endDate)) return;
    setFilters((prev) => ({
      ...prev,
      startDate: s.startDate || prev.startDate,
      endDate: s.endDate || prev.endDate,
      startTime:
        s.startTime != null && String(s.startTime).trim() !== ''
          ? String(s.startTime).trim()
          : s.startDate || s.endDate
            ? DEFAULT_REPORT_START_TIME
            : prev.startTime,
      endTime:
        s.endTime != null && String(s.endTime).trim() !== ''
          ? String(s.endTime).trim()
          : s.startDate || s.endDate
            ? DEFAULT_REPORT_END_TIME
            : prev.endTime
    }));
    setFetchTrigger((t) => t + 1);
  }, [location.state]);

  useEffect(() => {
    const interval = setInterval(() => {
      setFetchTrigger((t) => t + 1);
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const contentElement = document.querySelector('.report-content');
      if (contentElement) {
        setShowScrollTop(contentElement.scrollTop > 300);
      }
    };
    const contentElement = document.querySelector('.report-content');
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
      return () => contentElement.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const scrollToTop = () => {
    const contentElement = document.querySelector('.report-content');
    if (contentElement) {
      contentElement.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const dedupeByTime = (rows) => {
    if (!rows || rows.length === 0) return rows;
    const byTime = new Map();
    rows.forEach((row) => {
      const t = getRowTimeMs(row);
      if (!Number.isFinite(t)) return;
      const key = Math.floor(t / 1000);
      byTime.set(key, row);
    });
    return Array.from(byTime.values()).sort((a, b) => getRowTimeMs(b) - getRowTimeMs(a));
  };


  const filterTableRows = (rows, f) => {
    let out = rows;
    const min = f.minValue !== '' && !isNaN(parseFloat(f.minValue)) ? parseFloat(f.minValue) : null;
    const max = f.maxValue !== '' && !isNaN(parseFloat(f.maxValue)) ? parseFloat(f.maxValue) : null;
    const sensorType = f.sensorType;
    if (sensorType !== 'all' || min != null || max != null) {
      out = rows
        .map((row) => {
          const record = row;
          let value = null;
          if (sensorType === 'kseb') value = record.kseb?.phaseR ?? record.kseb?.phaseY ?? record.kseb?.phaseB;
          else if (sensorType === 'inverter') value = record.inverter?.phaseR ?? record.inverter?.phaseY ?? record.inverter?.phaseB;
          else if (sensorType === 'loadon') {
            const lo = (record.loadOn ?? '').toString().trim();
            if (!lo) return null;
            return record;
          } else if (sensorType === 'current') value = record.current?.c1 ?? record.current?.c2 ?? record.current?.c3;
          else if (sensorType === 'temperature') value = record.temperature1 ?? record.temperature2;
          else if (sensorType === 'battery') value = record.batteryVoltage;
          else if (sensorType === 'panel') value = record.panelVoltage1 ?? record.panelVoltage2 ?? record.panelVoltage3;
          if (sensorType !== 'all' && (value == null || (min != null && value < min) || (max != null && value > max))) return null;
          if (sensorType === 'all' && (min != null || max != null)) {
            const num = typeof value === 'number' ? value : parseFloat(value);
            if (!isNaN(num) && ((min != null && num < min) || (max != null && num > max))) return null;
          }
          return record;
        })
        .filter(Boolean);
    }
    return out;
  };

  const displayRows = useMemo(() => {
    const rows = filterTableRows(
      dedupeByTime(filterReadingsByRecordedAtWindow(readings, filters)),
      filters
    );
    return [...rows].sort((a, b) => {
      const ta = getRowTimeMs(a);
      const tb = getRowTimeMs(b);
      if (isNaN(ta) && isNaN(tb)) return 0;
      if (isNaN(ta)) return 1;
      if (isNaN(tb)) return -1;
      return tb - ta;
    });
  }, [readings, filters]);

  const effectivePageSize =
    pagination.pageSize >= 999999 ? Math.max(displayRows.length, 1) : pagination.pageSize;
  const totalPages =
    effectivePageSize <= 0 ? 1 : Math.max(1, Math.ceil(displayRows.length / effectivePageSize));
  const currentPageSafe = Math.min(pagination.page, totalPages);
  const pageStart = (currentPageSafe - 1) * effectivePageSize;
  const pageEnd = pageStart + effectivePageSize;
  const paginatedRows = displayRows.slice(pageStart, pageEnd);
  const totalCount = displayRows.length;

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: Math.min(prev.page, totalPages) }));
  }, [totalPages, displayRows.length]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'startDate') {
        if (value) {
          if (!prev.endDate) next.endDate = value;
          if (!String(next.startTime || '').trim()) next.startTime = DEFAULT_REPORT_START_TIME;
          if (!String(next.endTime || '').trim()) next.endTime = DEFAULT_REPORT_END_TIME;
        } else {
          next.startTime = '';
        }
      }
      if (field === 'endDate') {
        if (value) {
          if (!prev.startDate) next.startDate = value;
          if (!String(next.endTime || '').trim()) next.endTime = DEFAULT_REPORT_END_TIME;
          if (!String(next.startTime || '').trim()) next.startTime = DEFAULT_REPORT_START_TIME;
        } else {
          next.endTime = '';
        }
      }
      return next;
    });
  };

  const handleApplyFilters = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setFetchTrigger((t) => t + 1);
  };

  const handleResetFilters = () => {
    const cleared = {
      startDate: '',
      endDate: '',
      startTime: '',
      endTime: '',
      sensorType: 'all',
      minValue: '',
      maxValue: '',
      organizationId: ''
    };
    setFilters(cleared);
    filtersRef.current = cleared;
    setPagination((prev) => ({ ...prev, page: 1 }));
    setFetchTrigger((t) => t + 1);
  };

  const getExportParams = () => buildReportHistoryParams(filtersRef.current, null);

  const formatCell = (val, decimals = 0) => {
    if (val == null || val === '') return '—';
    const n = Number(val);
    return isNaN(n) ? String(val) : n.toFixed(decimals);
  };

  const formatVoltageCell = (val) => {
    if (val == null || val === '') return '—';
    if (typeof val === 'number') {
      if (isNaN(val)) return '—';
      return val.toFixed(0);
    }
    const s = String(val).trim();
    return s ? s : '—';
  };

  const formatRecordTime = (row) => formatRecordTimeFromRow(row);

  /** PDF/Excel: same display as table (DD-MM-YYYY HH:mm:ss). */
  const formatExportTime = (row) => formatRecordTimeFromRow(row);

  const getTableExportRows = () =>
    displayRows.map((row) => [
      formatExportTime(row),
      row.loadOn ?? '',
      row.kseb?.phaseR ?? '',
      row.kseb?.phaseY ?? '',
      row.kseb?.phaseB ?? '',
      row.inverter?.phaseR ?? '',
      row.inverter?.phaseY ?? '',
      row.inverter?.phaseB ?? '',
      row.current?.c1 ?? '',
      row.current?.c2 ?? '',
      row.current?.c3 ?? '',
      row.temperature1 ?? '',
      row.temperature2 ?? '',
      formatVoltageCell(row.batteryVoltage),
      formatVoltageCell(row.panelVoltage1),
      formatVoltageCell(row.panelVoltage2),
      formatVoltageCell(row.panelVoltage3),
      formatVoltageCell(row.panelVoltage4)
    ]);

  const handleExportPdf = () => {
    import('jspdf')
      .then(({ jsPDF }) => {
        import('jspdf-autotable').then((module) => {
          try {
            const autoTable = module.default;
            const doc = new jsPDF({ orientation: 'landscape' });
            doc.setFontSize(14);
            doc.text('Solar Monitor Report', 14, 15);
            const headers = [
              [
                'Time',
                'Load On',
                'KSEB R',
                'KSEB Y',
                'KSEB B',
                'Inv R',
                'Inv Y',
                'Inv B',
                'C1',
                'C2',
                'C3',
                'T1',
                'T2',
                'Batt V',
                'Panel V1',
                'Panel V2',
                'Panel V3',
                'Panel V4'
              ]
            ];
            const body = getTableExportRows().map((row) => row.map((c) => String(c)));
            autoTable(doc, { head: headers, body, startY: 22 });
            doc.save('SolarMonitor_Report.pdf');
          } catch (e) {
            window.print();
          }
        }).catch(() => window.print());
      })
      .catch(() => window.print());
  };

  const handleExportExcel = () => {
    import('xlsx').then((XLSX) => {
      try {
        const wsData = [
          [
            'Time',
            'Load On',
            'KSEB R',
            'KSEB Y',
            'KSEB B',
            'Inv R',
            'Inv Y',
            'Inv B',
            'C1',
            'C2',
            'C3',
            'T1',
            'T2',
            'Battery V',
            'Panel V1',
            'Panel V2',
            'Panel V3',
            'Panel V4'
          ],
          ...getTableExportRows()
        ];
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Report');
        XLSX.writeFile(wb, 'SolarMonitor_Report.xlsx');
      } catch (e) {
        setError('Excel export failed. Try CSV and open in Excel.');
      }
    }).catch(() => setError('Excel export requires xlsx package. Use CSV and open in Excel.'));
  };

  const handleExportCsv = async () => {
    setExporting(true);
    setError(null);
    setSuccess(false);
    try {
      await exportCsvReport(getExportParams());
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to export CSV.');
    } finally {
      setExporting(false);
    }
  };

  const handleRowsPerPageChange = (e) => {
    const val = e.target.value;
    const pageSize = val === 'All' ? 999999 : parseInt(val, 10);
    setPagination((prev) => ({ ...prev, pageSize, page: 1 }));
  };

  return (
    <div className="report-page report-generator">
      <div className="report-generator-container report-container">
        <div className="report-header">
          <button type="button" className="btn-back" onClick={() => navigate('/')}>
            ← Dashboard
          </button>
          <h2 className="report-title">Solar Monitor Report</h2>
          <div className="header-actions report-direct-buttons">
            <button
              type="button"
              className="btn-view-graphs"
              onClick={() =>
                navigate('/report/graphs', {
                  state: {
                    startDate: filters.startDate,
                    endDate: filters.endDate || filters.startDate,
                    startTime: (filters.startTime || '').trim() || DEFAULT_REPORT_START_TIME,
                    endTime: (filters.endTime || '').trim() || DEFAULT_REPORT_END_TIME
                  }
                })
              }
            >
              View Graphs
            </button>
            <button
              type="button"
              className="btn-view-working-hours"
              onClick={() =>
                navigate('/report/working-hours', {
                  state: {
                    startDate: filters.startDate,
                    endDate: filters.endDate || filters.startDate,
                    startTime: (filters.startTime || '').trim() || DEFAULT_REPORT_START_TIME,
                    endTime: (filters.endTime || '').trim() || DEFAULT_REPORT_END_TIME,
                    organizationId: filters.organizationId || ''
                  }
                })
              }
            >
              Working Hours
            </button>
            <button type="button" className="btn-apply" onClick={handleApplyFilters} disabled={loading}>
              Apply Filters
            </button>
            <button type="button" className="btn-reset" onClick={handleResetFilters} disabled={loading}>
              Reset
            </button>
          </div>
        </div>

        <div className="report-filters-row">
          <div className="filter-card">
            <h3 className="filter-card-title">DATE & TIME</h3>
            <div className="filter-card-fields">
              <div className="form-group">
                <label htmlFor="rp-startDate">Start Date</label>
                <input
                  type="date"
                  id="rp-startDate"
                  value={filters.startDate}
                  max={today}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="rp-endDate">End Date</label>
                <input
                  type="date"
                  id="rp-endDate"
                  value={filters.endDate}
                  max={today}
                  min={filters.startDate || undefined}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="rp-startTime">Start Time</label>
                <input
                  type="time"
                  id="rp-startTime"
                  value={filters.startTime}
                  title="Start of range: 00:00 is midnight (12 AM)"
                  onChange={(e) => handleFilterChange('startTime', e.target.value)}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="rp-endTime">End Time</label>
                <input
                  type="time"
                  id="rp-endTime"
                  value={filters.endTime}
                  title="End of range: 23:59 is 11:59 PM; API uses 23:59:59 for that day"
                  onChange={(e) => handleFilterChange('endTime', e.target.value)}
                  className="form-input"
                />
              </div>
            </div>
          </div>
          <div className="filter-card">
            <h3 className="filter-card-title">SENSOR COMPONENT</h3>
            <div className="filter-card-fields">
              <div className="form-group">
                <label htmlFor="rp-sensorType">Sensor Type</label>
                <select
                  id="rp-sensorType"
                  value={filters.sensorType}
                  onChange={(e) => handleFilterChange('sensorType', e.target.value)}
                  className="form-input"
                >
                  {SENSOR_TYPES.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="rp-minValue">Min Value</label>
                <input
                  type="text"
                  id="rp-minValue"
                  value={filters.minValue}
                  onChange={(e) => handleFilterChange('minValue', e.target.value)}
                  className="form-input"
                  placeholder="Min"
                />
              </div>
              <div className="form-group">
                <label htmlFor="rp-maxValue">Max Value</label>
                <input
                  type="text"
                  id="rp-maxValue"
                  value={filters.maxValue}
                  onChange={(e) => handleFilterChange('maxValue', e.target.value)}
                  className="form-input"
                  placeholder="Max"
                />
              </div>
            </div>
          </div>
        </div>

        {error && <div className="report-error error-message">{error}</div>}
        {success && <div className="success-message">Report downloaded successfully!</div>}

        <div className="report-content">
          <div className="report-info">
            <span>Total Records: {loading ? '…' : totalCount}</span>
            <span>
              Page {currentPageSafe} of {totalPages}
            </span>
            <span className="report-table-count">
              Report table
              {loading ? ' (loading…)' : ` (${totalCount} record${totalCount !== 1 ? 's' : ''})`}
            </span>
          </div>

          <div className="report-toolbar">
            <div className="report-toolbar-export report-print-actions">
              <span className="report-toolbar-label report-print-label">Export / Print:</span>
              <button
                type="button"
                className="btn-export-pdf btn-print-option btn-pdf"
                onClick={handleExportPdf}
                disabled={exporting || loading || displayRows.length === 0}
              >
                {exporting ? 'Exporting...' : 'PDF'}
              </button>
              <button
                type="button"
                className="btn-export-excel btn-print-option btn-excel"
                onClick={handleExportExcel}
                disabled={exporting || loading || displayRows.length === 0}
              >
                {exporting ? 'Exporting...' : 'Excel'}
              </button>
              <button
                type="button"
                className="btn-export-csv btn-print-option btn-csv"
                onClick={handleExportCsv}
                disabled={exporting || loading}
              >
                CSV
              </button>
              <button
                type="button"
                className="btn-export-clear btn-clear"
                onClick={handleResetFilters}
                disabled={loading}
              >
                Clear
              </button>
            </div>
            <div className="report-toolbar-rows">
              <span className="report-toolbar-label">Rows per page:</span>
              <select
                className="report-rows-select report-limit-select"
                value={pagination.pageSize >= 999999 ? 'All' : pagination.pageSize}
                onChange={handleRowsPerPageChange}
                disabled={loading}
              >
                <option value={10}>10</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={500}>500</option>
                <option value="All">All</option>
              </select>
            </div>
          </div>

          <div className="report-table-container report-table-wrapper">
            {loading && (
              <div className="report-loading-overlay" role="status" aria-live="polite">
                Loading report data...
              </div>
            )}
            <table className="report-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Load On</th>
                  <th>KSEB R</th>
                  <th>KSEB Y</th>
                  <th>KSEB B</th>
                  <th>Inv R</th>
                  <th>Inv Y</th>
                  <th>Inv B</th>
                  <th>C1</th>
                  <th>C2</th>
                  <th>C3</th>
                  <th>T1</th>
                  <th>T2</th>
                  <th>Battery V</th>
                  <th>Panel V1</th>
                  <th>Panel V2</th>
                  <th>Panel V3</th>
                  <th>Panel V4</th>
                </tr>
              </thead>
              <tbody>
                {!loading && displayRows.length === 0 ? (
                  <tr>
                    <td colSpan={18} className="report-empty table-no-data">
                      No records found for the selected filters. Adjust dates or filters and click Apply Filters.
                    </td>
                  </tr>
                ) : (
                  paginatedRows.map((row, idx) => (
                    <tr key={row.id ?? `${row.recordedAt ?? ''}-${pageStart + idx}`}>
                      <td>{formatRecordTime(row)}</td>
                      <td>{row.loadOn != null && String(row.loadOn).trim() !== '' ? String(row.loadOn) : '—'}</td>
                      <td>{formatCell(row.kseb?.phaseR)}</td>
                      <td>{formatCell(row.kseb?.phaseY)}</td>
                      <td>{formatCell(row.kseb?.phaseB)}</td>
                      <td>{formatCell(row.inverter?.phaseR)}</td>
                      <td>{formatCell(row.inverter?.phaseY)}</td>
                      <td>{formatCell(row.inverter?.phaseB)}</td>
                      <td>{formatCell(row.current?.c1)}</td>
                      <td>{formatCell(row.current?.c2)}</td>
                      <td>{formatCell(row.current?.c3)}</td>
                      <td>{formatCell(row.temperature1, 1)}</td>
                      <td>{formatCell(row.temperature2, 1)}</td>
                      <td>{formatVoltageCell(row.batteryVoltage)}</td>
                      <td>{formatVoltageCell(row.panelVoltage1)}</td>
                      <td>{formatVoltageCell(row.panelVoltage2)}</td>
                      <td>{formatVoltageCell(row.panelVoltage3)}</td>
                      <td>{formatVoltageCell(row.panelVoltage4)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="report-pagination">
            <button
              type="button"
              className="btn-pagination btn-pagination-prev"
              onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
              disabled={currentPageSafe <= 1 || loading}
            >
              Previous
            </button>
            <span>
              Page {currentPageSafe} of {totalPages} — Showing {pageStart + 1}–{Math.min(pageEnd, displayRows.length)} of {displayRows.length}
            </span>
            <button
              type="button"
              className="btn-pagination btn-pagination-next"
              onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(totalPages, prev.page + 1) }))}
              disabled={currentPageSafe >= totalPages || loading}
            >
              Next
            </button>
          </div>
        </div>

        {showScrollTop && (
          <button type="button" className="btn-scroll-top" onClick={scrollToTop} title="Scroll to top">
            ↑
          </button>
        )}
      </div>
    </div>
  );
}

export default ReportPage;
